import React from "react";
import { Anchor, Box, Button, Group, Stack, Text } from "@mantine/core";
import { IconArrowLeft, IconCheck, IconX } from "@tabler/icons-react";

/** The commission form's two screens. */
export type CommissionFormScreen = "form" | "service-rates";

export interface CommissionFormFrameProps {
  screen: CommissionFormScreen;
  /** A screen's own heading (the form screen leaves it to the host). */
  title?: string;
  /** Back to the previous screen. */
  onBack?: () => void;
  /** Kept in view above the scrolling content (e.g. a search box). */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  /** The screen's buttons. */
  footer: React.ReactNode;
}

/**
 * Lays out one screen of the commission form. Hosts pick the chrome — inline
 * in a page or modal, or as a Commission Rate drawer screen with a fixed
 * header and footer. Called as a function (not rendered as a component) so a
 * host can pass an inline closure without remounting the form's inputs.
 */
export type CommissionFormFrame = (
  props: CommissionFormFrameProps,
) => React.ReactElement;

/** The default: everything stacked in place (tier page, legacy modal). */
export const inlineFormFrame: CommissionFormFrame = ({
  title,
  onBack,
  toolbar,
  children,
  footer,
}) => (
  <Stack gap="sm">
    {onBack && (
      <Group gap="xs">
        <Anchor component="button" type="button" size="sm" onClick={onBack}>
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={14} />
            Back
          </Group>
        </Anchor>
      </Group>
    )}
    {title && <Text fw={600}>{title}</Text>}
    {toolbar}
    {children}
    {footer}
  </Stack>
);

export interface CommissionFormActionsProps {
  /** Rendered at the left (e.g. a tier template's "Remove"). */
  leftSection?: React.ReactNode;
  /** Omit to hide the secondary button. */
  cancelLabel?: string;
  /** "Back" reads as a step back (arrow); anything else as dismiss (×). */
  onCancel?: () => void;
  /** Omit to hide the primary button (read-only forms). */
  submitLabel?: string;
  onSubmit?: () => void;
  isLoading?: boolean;
  submitDisabled?: boolean;
}

/** The commission form's footer: secondary Cancel/Back, primary Save. */
export const CommissionFormActions: React.FC<CommissionFormActionsProps> = ({
  leftSection,
  cancelLabel,
  onCancel,
  submitLabel,
  onSubmit,
  isLoading = false,
  submitDisabled = false,
}) => (
  <Group justify="space-between">
    <Box>{leftSection}</Box>
    <Group gap="sm">
      {cancelLabel && (
        <Button
          variant="light"
          color="gray"
          leftSection={
            cancelLabel === "Back" ? (
              <IconArrowLeft size={14} />
            ) : (
              <IconX size={14} />
            )
          }
          onClick={onCancel}
          disabled={isLoading}
          size="sm"
        >
          {cancelLabel}
        </Button>
      )}
      {submitLabel && (
        <Button
          color="var(--brand-teal)"
          leftSection={<IconCheck size={14} />}
          onClick={onSubmit}
          loading={isLoading}
          disabled={submitDisabled}
          size="sm"
        >
          {submitLabel}
        </Button>
      )}
    </Group>
  </Group>
);
